/**
 * FERP PDF Toolbar Component (DRR-079)
 *
 * Mantine toolbar with annotation mode buttons, zoom controls,
 * page navigation, signature button, and export/download button.
 *
 * FERP Constraints:
 * - Mantine v8.x ONLY
 * - No hardcoded business logic — pure UI controls
 */

import {
  Group,
  ActionIcon,
  Text,
  TextInput,
  Tooltip,
  SegmentedControl,
  Divider,
  Select,
  Paper,
} from '@mantine/core';
import {
  IconChevronLeft,
  IconChevronRight,
  IconZoomIn,
  IconZoomOut,
  IconZoomReset,
  IconHighlight,
  IconMessage,
  IconRubberStamp,
  IconPointer,
  IconPencilOff,
  IconSignature,
  IconDownload,
  IconSearch,
} from '@tabler/icons-react';
import type { AnnotationMode, PdfViewerState, StampValue } from './types';

interface PDFToolbarProps {
  viewerState: PdfViewerState;
  onPageChange: (page: number) => void;
  onZoomChange: (scale: number) => void;
  onAnnotationModeChange?: (mode: AnnotationMode) => void;
  onStampChange: (stamp: StampValue) => void;
  onSearchChange: (query: string) => void;
  onRequestSign?: () => void;
  onRequestExport?: () => void;
}

const ZOOM_STEPS = [0.5, 0.75, 1.0, 1.25, 1.5, 2.0];

export function PDFToolbar({
  viewerState,
  onPageChange,
  onZoomChange,
  onAnnotationModeChange,
  onStampChange,
  onSearchChange,
  onRequestSign,
  onRequestExport,
}: PDFToolbarProps) {
  const { numPages, currentPage, scale, annotationMode, searchQuery } = viewerState;

  const zoomIn = () => {
    const nextStep = ZOOM_STEPS.find((s) => s > scale);
    onZoomChange(nextStep ?? scale + 0.25);
  };

  const zoomOut = () => {
    const prevStep = [...ZOOM_STEPS].reverse().find((s) => s < scale);
    onZoomChange(prevStep ?? Math.max(0.25, scale - 0.25));
  };

  return (
    <Paper withBorder p="xs" mb={0} style={{ borderBottom: 'none', borderRadius: '4px 4px 0 0' }}>
      <Group gap="xs" wrap="nowrap">
        {/* Page Navigation */}
        <Group gap={4}>
          <Tooltip label="Previous page">
            <ActionIcon
              variant="subtle"
              size="sm"
              disabled={currentPage <= 1}
              onClick={() => onPageChange(currentPage - 1)}
            >
              <IconChevronLeft size={16} />
            </ActionIcon>
          </Tooltip>
          <Text size="sm" fw={500}>
            {currentPage} / {numPages || '?'}
          </Text>
          <Tooltip label="Next page">
            <ActionIcon
              variant="subtle"
              size="sm"
              disabled={currentPage >= numPages}
              onClick={() => onPageChange(currentPage + 1)}
            >
              <IconChevronRight size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>

        <Divider orientation="vertical" />

        {/* Zoom Controls */}
        <Group gap={4}>
          <Tooltip label="Zoom out">
            <ActionIcon variant="subtle" size="sm" onClick={zoomOut}>
              <IconZoomOut size={16} />
            </ActionIcon>
          </Tooltip>
          <Text size="xs" w={45} ta="center">
            {Math.round(scale * 100)}%
          </Text>
          <Tooltip label="Zoom in">
            <ActionIcon variant="subtle" size="sm" onClick={zoomIn}>
              <IconZoomIn size={16} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Reset zoom">
            <ActionIcon variant="subtle" size="sm" onClick={() => onZoomChange(1.0)}>
              <IconZoomReset size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>

        <Divider orientation="vertical" />

        {/* Annotation Tools */}
        {onAnnotationModeChange && (
          <>
            <Group gap={4}>
              <Tooltip label="Select mode">
                <ActionIcon
                  variant={annotationMode === null ? 'filled' : 'subtle'}
                  size="sm"
                  onClick={() => onAnnotationModeChange(null)}
                >
                  <IconPointer size={16} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Highlight">
                <ActionIcon
                  variant={annotationMode === 'highlight' ? 'filled' : 'subtle'}
                  color="yellow"
                  size="sm"
                  onClick={() =>
                    onAnnotationModeChange(
                      annotationMode === 'highlight' ? null : 'highlight',
                    )
                  }
                >
                  <IconHighlight size={16} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Text comment">
                <ActionIcon
                  variant={annotationMode === 'text' ? 'filled' : 'subtle'}
                  color="orange"
                  size="sm"
                  onClick={() =>
                    onAnnotationModeChange(annotationMode === 'text' ? null : 'text')
                  }
                >
                  <IconMessage size={16} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Stamp">
                <ActionIcon
                  variant={annotationMode === 'stamp' ? 'filled' : 'subtle'}
                  color="green"
                  size="sm"
                  onClick={() =>
                    onAnnotationModeChange(annotationMode === 'stamp' ? null : 'stamp')
                  }
                >
                  <IconRubberStamp size={16} />
                </ActionIcon>
              </Tooltip>
            </Group>

            {annotationMode === 'stamp' && (
              <Select
                size="xs"
                w={120}
                data={[
                  { value: 'APPROVED', label: 'Approved' },
                  { value: 'REJECTED', label: 'Rejected' },
                  { value: 'REVIEWED', label: 'Reviewed' },
                ]}
                value={viewerState.selectedStamp}
                onChange={(v) => v && onStampChange(v as StampValue)}
                allowDeselect={false}
              />
            )}

            <Divider orientation="vertical" />
          </>
        )}

        {/* Search */}
        <TextInput
          size="xs"
          placeholder="Search text..."
          leftSection={<IconSearch size={14} />}
          value={searchQuery}
          onChange={(e) => onSearchChange(e.currentTarget.value)}
          w={160}
        />

        <div style={{ flex: 1 }} />

        {/* Actions */}
        <Group gap={4}>
          {onRequestSign && (
            <Tooltip label="Sign document">
              <ActionIcon variant="light" color="violet" size="sm" onClick={onRequestSign}>
                <IconSignature size={16} />
              </ActionIcon>
            </Tooltip>
          )}
          {onRequestExport && (
            <Tooltip label="Export PDF with annotations">
              <ActionIcon variant="light" size="sm" onClick={onRequestExport}>
                <IconDownload size={16} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      </Group>
    </Paper>
  );
}
